import type { AutomationEvidence } from "../types";

/**
 * The same evaluation, stated in operations units instead of model units.
 *
 * AP and ROC-AUC are the right numbers for choosing a model and the wrong numbers for
 * deciding whether to staff a team. This panel converts the identical measurements into
 * cases per day and people-equivalents, because that is the unit the decision is actually
 * made in.
 *
 * It also shows two operating policies rather than one. Both were computed in the original
 * evaluation - `recall_at_review_rate` was a required metric before any result was seen, so
 * this is not a metric chosen after the fact to look better. What changes between them is
 * what gets held fixed: a miss-rate floor, or a review budget. The project's application
 * gate was defined on the first, and failed there; the second is shown because it is the
 * policy an operations team actually lives under, not because it flatters the model.
 */

interface Props {
  evidence: AutomationEvidence;
}

const CAPACITY_POLICY_REVIEW_RATE = 0.1;

export function OperationalImpact({ evidence }: Props) {
  const {
    rule,
    ml,
    assumed_daily_transactions: daily,
    assumed_daily_review_capacity: capacity,
  } = evidence;

  const staff = (reviewsPerDay: number) => reviewsPerDay / capacity;

  const everythingManual = daily;
  const recallPolicyReviews = rule.projected_daily_reviews;
  const capacityPolicyReviews = Math.round(daily * CAPACITY_POLICY_REVIEW_RATE);

  return (
    <section className="card">
      <h3>🧮 사람 몇 명이 필요한 일일까?</h3>
      <p className="muted">
        같은 평가 결과를 &quot;모델 점수&quot;가 아니라 <strong>&quot;사람 몇 명이
        필요한가&quot;</strong>로 바꿔서 본 거예요. 하루 거래 {daily.toLocaleString()}건, 담당자
        1명이 하루 {capacity.toLocaleString()}건 처리를 <strong>가정</strong>했습니다(실측
        아님).
      </p>

      <table className="evidence-table impact-table">
        <thead>
          <tr>
            <th>운영 방식</th>
            <th>사람이 봐야 하는 건수/일</th>
            <th>필요 인원(환산)</th>
            <th>자동 처리율(STP)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>전부 사람이 검토</td>
            <td>{everythingManual.toLocaleString()}건</td>
            <td>{staff(everythingManual).toFixed(0)}명</td>
            <td>0%</td>
          </tr>
          <tr>
            <td>
              정책 A — <strong>미탐률 고정</strong>
              <br />
              <span className="muted">사기 95%를 반드시 잡는다는 조건</span>
            </td>
            <td>{recallPolicyReviews.toLocaleString()}건</td>
            <td>{staff(recallPolicyReviews).toFixed(0)}명</td>
            <td>{(rule.approve_rate * 100).toFixed(1)}%</td>
          </tr>
          <tr className="highlight">
            <td>
              정책 B — <strong>검토 용량 고정</strong>
              <br />
              <span className="muted">사람이 볼 수 있는 만큼만 본다는 조건</span>
            </td>
            <td>{capacityPolicyReviews.toLocaleString()}건</td>
            <td>{staff(capacityPolicyReviews).toFixed(0)}명</td>
            <td>{((1 - CAPACITY_POLICY_REVIEW_RATE) * 100).toFixed(0)}%</td>
          </tr>
        </tbody>
      </table>

      <p className="callout">
        <strong>두 정책은 먼저 정하는 게 서로 달라요.</strong> 정책 A는 &quot;사기를 몇 %
        잡을지&quot;를 먼저 정하고 사람 수를 거기에 맞추는 방식, 정책 B는 &quot;사람은
        이만큼뿐&quot;을 먼저 정하고 잡히는 만큼 잡는 방식이에요. 어느 쪽이 맞는지는 은행이 정할
        문제고, 이 프로젝트가 만든 건 <strong>그 선택을 숫자로 비교할 수 있게 하는
        장치</strong>입니다.
      </p>

      <h4>정책 B(검토 용량 고정)에서 모델이 만드는 차이</h4>
      <p className="muted">
        인력을 늘리지 않고 같은 {capacityPolicyReviews.toLocaleString()}건만 검토했을 때, 그
        안에서 잡아내는 사기 비율입니다.
      </p>
      <table className="evidence-table">
        <tbody>
          <tr>
            <td>규칙 기반 (현재 서빙 중)</td>
            <td>{(rule.recall_at_10pct_review * 100).toFixed(1)}%</td>
          </tr>
          <tr>
            <td>학습 모델 (실험)</td>
            <td>
              <strong>{(ml.recall_at_10pct_review * 100).toFixed(1)}%</strong>
            </td>
          </tr>
          <tr>
            <td>차이</td>
            <td>
              <strong>
                +
                {(
                  ((ml.recall_at_10pct_review - rule.recall_at_10pct_review) /
                    rule.recall_at_10pct_review) *
                  100
                ).toFixed(0)}
                %
              </strong>{" "}
              (같은 인력으로 사기를 더 잡음)
            </td>
          </tr>
        </tbody>
      </table>

      <p className="warn-banner">
        그런데도 <strong>학습 모델은 투입하지 않았어요.</strong> 시험 보기 전에 합격 기준을
        먼저 종이에 적어뒀는데(정책 A 기준), 그 기준으로는 불합격이었거든요. 시험 끝나고 나서
        &quot;사실 다른 기준으로 하면 합격인데요&quot;라고 기준을 바꿔치기하면 그 시험은 의미가
        없어져요. 그래서 불합격 성적표도 그대로 남겨뒀습니다.
      </p>
    </section>
  );
}
